import { Fragment, ReactElement, ReactNode } from "react";

import styled from "@emotion/styled";

import { Box } from "@/components/layout/flexbox";
import { StandaloneCheckbox } from "@/components/pieces/form/StandaloneCheckbox";
import {
  INNER_BORDER_COLOR,
  LIGHT_BACKGROUND,
  MIDTEXT_COLOR,
  SUBTEXT_COLOR,
} from "@/css/constants";

const CELL_PADDING = 12;
const ROW_PADDING_X = 24;

const StyledTable = styled("table")`
  width: 100%;

  th {
    color: ${MIDTEXT_COLOR};
    background-color: ${LIGHT_BACKGROUND};
    padding-top: 16px;
    padding-bottom: 16px;
    padding-left: ${CELL_PADDING}px;
    padding-right: ${CELL_PADDING}px;
    &:first-of-type {
      padding-left: ${ROW_PADDING_X}px;
    }
    &:last-of-type {
      padding-right: ${ROW_PADDING_X}px;
    }
  }
  td {
    padding: ${CELL_PADDING}px;
    &:first-of-type {
      padding-left: ${ROW_PADDING_X}px;
    }
    &:last-of-type {
      padding-right: ${ROW_PADDING_X}px;
    }
  }

  tfoot th td {
    padding: 10px ${CELL_PADDING}px;
  }

  td#no-data {
    color: ${SUBTEXT_COLOR};
  }

  @media only screen and (max-width: 640px) {
    th {
      &:first-of-type {
        padding-left: ${CELL_PADDING}px;
      }
      &:last-of-type {
        padding-right: ${CELL_PADDING}px;
      }
    }

    td {
      &:first-of-type {
        padding-left: ${CELL_PADDING}px;
      }
      &:last-of-type {
        padding-right: ${CELL_PADDING}px;
      }
    }
  }
`;

interface RenderableItem {
  id: string;
}

interface SimpleTableProps<T extends RenderableItem> {
  columns: {
    id: string;
    header: ReactNode;
    renderCell?: (item: T) => ReactElement;
  }[];
  rows: T[];
  showCheckBox?: boolean;
  selectedIds?: string[] | number[];
  checkedAll?: boolean;
  onCheckAll?: (props: { checked: boolean }) => void;
  onCheckRow?: (rowData: { data: T; checked: boolean }) => void;
  isRowChecked?: (item: T) => boolean;
  emptyText?: ReactNode;
  footerText?: ReactNode;
}

export function SimpleTable<T extends RenderableItem>({
  columns,
  rows,
  showCheckBox = false,
  selectedIds,
  checkedAll = false,
  onCheckAll,
  onCheckRow,
  isRowChecked,
  emptyText = "No data available",
  footerText,
}: SimpleTableProps<T>) {
  const selectedIdsSet = new Set<string | number>(selectedIds);

  const renderHeader = () => {
    return (
      <tr>
        {showCheckBox && (
          <th style={{ paddingRight: 0, width: "20px" }}>
            <StandaloneCheckbox
              checked={checkedAll}
              onChange={(e) => {
                onCheckAll?.({
                  checked: (e.target as HTMLInputElement).checked,
                });
              }}
            />
          </th>
        )}
        {columns.map((column) => (
          <Fragment key={column.id}>{column.header}</Fragment>
        ))}
      </tr>
    );
  };

  const renderFooter = () => {
    return (
      <tr>
        <th colSpan={columns.length + (showCheckBox ? 1 : 0)}>{footerText}</th>
      </tr>
    );
  };

  const renderRows = () => {
    return rows
      .filter((row) => row !== null && row !== undefined)
      .map((row) => (
        <tr key={row.id}>
          {showCheckBox && (
            <td style={{ paddingRight: 0 }}>
              <StandaloneCheckbox
                checked={
                  isRowChecked ? isRowChecked(row) : selectedIdsSet.has(row.id)
                }
                onChange={(e) => {
                  onCheckRow?.({
                    data: row,
                    checked: (e.target as HTMLInputElement).checked,
                  });
                }}
              />
            </td>
          )}
          {columns.map((column) => (
            <Fragment key={column.id}>
              {column.renderCell ? (
                column.renderCell(row)
              ) : (
                <td>{row as unknown as ReactNode}</td>
              )}
            </Fragment>
          ))}
        </tr>
      ));
  };

  return (
    <Box
      css={{
        borderRadius: 8,
        border: `1px solid ${INNER_BORDER_COLOR}`,
        overflow: "hidden",
      }}
    >
      <StyledTable>
        <thead>{renderHeader()}</thead>
        <tbody>
          {rows.length ? (
            renderRows()
          ) : (
            <tr>
              <td
                id="no-data"
                colSpan={columns.length + (showCheckBox ? 1 : 0)}
              >
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
        {footerText ? <tfoot>{renderFooter()}</tfoot> : null}
      </StyledTable>
    </Box>
  );
}
